import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "search",
    "street",
    "suburb",
    "city",
    "state",
    "postcode",
    "country",
    "source",
    "formFields",
    "display",
    "displayText",
    "searchContainer",
    "submitButton",
    "errorMessage"
  ]
  static values = {
    apiKey: String,
    showForm: Boolean,
    required: { type: Boolean, default: false },
    autoSubmit: { type: Boolean, default: true }
  }
  
  connect() {
    this.sessionToken = null  // Will be created when user starts typing
    this.loadGoogleMapsScript()
  }
  
  loadGoogleMapsScript() {
    // Check if Google Maps is already loaded
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      this.initializeAutocomplete()
      return
    }
    
    // Check if script is already being loaded
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      this.waitForGoogleMaps()
      return
    }
    
    // Load script with async parameter
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKeyValue}&libraries=places&loading=async`
    script.async = true
    script.defer = true
    script.onload = () => this.waitForGoogleMaps()
    
    document.head.appendChild(script)
  }
  
  waitForGoogleMaps() {
    if (typeof google !== 'undefined' && google.maps && google.maps.places && google.maps.places.Autocomplete) {
      this.initializeAutocomplete()
    } else {
      setTimeout(() => this.waitForGoogleMaps(), 100)
    }
  }
  
  initializeAutocomplete() {
    // Create session token for billing optimization
    this.createSessionToken()

    this.autocomplete = new google.maps.places.Autocomplete(this.searchTarget, {
      types: ['address'],
      componentRestrictions: { country: 'AU' },
      fields: ['address_components', 'formatted_address']  // Only fetch needed fields
    })

    // Set session token
    if (this.sessionToken) {
      this.autocomplete.setOptions({ sessionToken: this.sessionToken })
    }

    this.autocomplete.addListener('place_changed', () => {
      this.handlePlaceSelect()
    })
  }

  createSessionToken() {
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      this.sessionToken = new google.maps.places.AutocompleteSessionToken()
    }
  }
  
  handlePlaceSelect() {
    const place = this.autocomplete.getPlace()
    if (!place.address_components) return

    const addressData = this.parseAddressComponents(place.address_components)
    this.populateAddressFields(addressData)
    this.updateUIAfterSelection(place.formatted_address || this.buildDisplayAddress(addressData))
    if (this.autoSubmitValue) {
      this.submitAddressToServer()
    }
    this.createSessionToken()
  }

  updateUIAfterSelection(displayAddress) {
    this.showAddressDisplay(displayAddress)
    this.hideSearchContainer()
    this.hideFormFields()
  }

  async submitAddressToServer() {
    const { patch } = await import('@rails/request.js')
    await patch(this.updateAddressPath(), {
      query: { order_id: this.getOrderId() },
      body: JSON.stringify(this.buildRequestBody()),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    })
  }

  updateAddressPath() {
    return '/flow/booking/guest_services/update_address'
  }

  buildRequestBody() {
    return {
      order_id: this.getOrderId(),
      order_item_id: this.getOrderItemId(),
      order_item_index: this.getOrderItemIndex(),
      order: {
        user_attributes: {
          id: this.getUserId(),
          address_attributes: this.buildAddressAttributes()
        }
      }
    }
  }

  buildAddressAttributes() {
    return {
      ...this.extractFieldValues(),
      ...this.getAddressIdIfPresent()
    }
  }

  extractFieldValues() {
    return this.addressFieldMappings()
      .filter(([_, config]) => config.hasTarget)
      .reduce((acc, [key, config]) => ({
        ...acc,
        [key]: this[config.target].value
      }), {})
  }

  addressFieldMappings() {
    return [
      ['street', { target: 'streetTarget', hasTarget: this.hasStreetTarget }],
      ['suburb', { target: 'suburbTarget', hasTarget: this.hasSuburbTarget }],
      ['city', { target: 'cityTarget', hasTarget: this.hasCityTarget }],
      ['state', { target: 'stateTarget', hasTarget: this.hasStateTarget }],
      ['postcode', { target: 'postcodeTarget', hasTarget: this.hasPostcodeTarget }],
      ['country', { target: 'countryTarget', hasTarget: this.hasCountryTarget }],
      ['source', { target: 'sourceTarget', hasTarget: this.hasSourceTarget }]
    ]
  }

  getAddressIdIfPresent() {
    const addressId = this.findHiddenFieldValue('order[user_attributes][address_attributes][id]')
    return addressId ? { id: addressId } : {}
  }

  getUserId() {
    return this.findHiddenFieldValue('order[user_attributes][id]')
  }

  getOrderId() {
    return this.getFrameUrlParam('order_id') || this.urlParam('order_id')
  }

  getOrderItemId() {
    return this.getFrameUrlParam('order_item_id')
  }

  getOrderItemIndex() {
    return this.getFrameUrlParam('order_item_index') || '0'
  }

  getFrameUrlParam(key) {
    const frame = this.element.closest('turbo-frame')
    if (!frame?.src) return null

    const url = new URL(frame.src, window.location.origin)
    return url.searchParams.get(key)
  }

  urlParam(key) {
    return new URLSearchParams(window.location.search).get(key)
  }

  findHiddenFieldValue(name) {
    return document.querySelector(`input[name="${name}"]`)?.value
  }

  showSearchInput() {
    this.hideAddressDisplay()
    this.showSearchContainer()
    this.searchTarget.focus()

    // Create new session token when user reopens search
    this.createSessionToken()
    if (this.autocomplete && this.sessionToken) {
      this.autocomplete.setOptions({ sessionToken: this.sessionToken })
    }
  }
  
  showAddressDisplay(address) {
    if (this.hasDisplayTarget && this.hasDisplayTextTarget) {
      this.displayTextTarget.textContent = address
      this.displayTarget.classList.remove('hidden')
    }
  }
  
  hideAddressDisplay() {
    if (this.hasDisplayTarget) {
      this.displayTarget.classList.add('hidden')
    }
  }
  
  showSearchContainer() {
    if (this.hasSearchContainerTarget) {
      this.searchContainerTarget.classList.remove('hidden')
    }
  }
  
  hideSearchContainer() {
    if (this.hasSearchContainerTarget) {
      this.searchContainerTarget.classList.add('hidden')
      // Clear the search input and remove focus
      this.searchTarget.value = ''
      this.searchTarget.blur()
    }
  }
  
  buildDisplayAddress(addressData) {
    const parts = [
      addressData.street,
      addressData.suburb,
      addressData.state,
      addressData.postcode
    ].filter(part => part && part.trim())
    
    return parts.join(', ')
  }
  
  hideFormFields() {
    if (this.hasFormFieldsTarget) {
      this.formFieldsTarget.classList.add('hidden')
    }
  }
  
  showFormFields() {
    if (this.hasFormFieldsTarget) {
      this.formFieldsTarget.classList.remove('hidden')
    }
  }
  
  parseAddressComponents(components) {
    const addressMap = {
      street_number: '',
      route: '',
      subpremise: '', // Unit/apartment number
      locality: '', // Suburb
      administrative_area_level_2: '', // City  
      administrative_area_level_1: '', // State
      postal_code: '',
      country: ''
    }
    
    // Australian state abbreviations mapping as fallback
    const australianStates = {
      'new south wales': 'NSW',
      'victoria': 'VIC', 
      'queensland': 'QLD',
      'western australia': 'WA',
      'south australia': 'SA',
      'tasmania': 'TAS',
      'northern territory': 'NT',
      'australian capital territory': 'ACT'
    }
    
    components.forEach(component => {
      const type = component.types[0]
      if (addressMap.hasOwnProperty(type)) {
        // For state, use short_name if available, otherwise map from long_name
        if (type === 'administrative_area_level_1') {
          if (component.short_name && component.short_name !== component.long_name) {
            addressMap[type] = component.short_name
          } else {
            const longNameLower = component.long_name.toLowerCase()
            addressMap[type] = australianStates[longNameLower] || component.long_name
          }
        } else {
          addressMap[type] = component.long_name
        }
      }
    })
    
    return {
      street: this.buildStreetAddress(addressMap),
      suburb: addressMap.locality,
      city: addressMap.administrative_area_level_2,
      state: addressMap.administrative_area_level_1,
      postcode: addressMap.postal_code,
      country: addressMap.country
    }
  }
  
  buildStreetAddress(addressMap) {
    let street = ''
    
    if (addressMap.subpremise) {
      street += `${addressMap.subpremise}/`
    }
    
    if (addressMap.street_number) {
      street += `${addressMap.street_number} `
    }
    
    if (addressMap.route) {
      street += addressMap.route
    }
    
    return street.trim()
  }
  
  populateAddressFields(addressData) {
    if (this.hasStreetTarget && addressData.street) {
      this.streetTarget.value = addressData.street
      this.streetTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }

    if (this.hasSuburbTarget && addressData.suburb) {
      this.suburbTarget.value = addressData.suburb
      this.suburbTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }

    if (this.hasCityTarget && addressData.city) {
      this.cityTarget.value = addressData.city
      this.cityTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }

    if (this.hasStateTarget && addressData.state) {
      this.stateTarget.value = addressData.state
      this.stateTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }

    if (this.hasPostcodeTarget && addressData.postcode) {
      this.postcodeTarget.value = addressData.postcode
      this.postcodeTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }

    if (this.hasCountryTarget && addressData.country) {
      this.countryTarget.value = addressData.country
      this.countryTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }

    // Mark address as Google-sourced when populated from autocomplete
    this.setAddressSource('google')
  }

  setAddressSource(source) {
    if (this.hasSourceTarget) {
      this.sourceTarget.value = source
    }
  }

  // Called when user manually edits any address field
  markAsManualEntry() {
    this.setAddressSource('manual')
  }
}
